import React, { useState } from "react";
import { useTotpSetup } from "../../hooks/useTotpSetup";
import { useTotpReplace } from "../../hooks/useTotpReplace";
import { useTotpConfirm, MfaAlreadyEnabledError } from "../../hooks/useTotpConfirm";
import { TotpStartStep } from "./TotpStartStep";
import { TotpConfirmStep } from "./TotpConfirmStep";
import { RecoveryCodesStep } from "./RecoveryCodesStep";
import { AuthenticatorAppsHint } from "./AuthenticatorAppsHint";

type WizardStep = "start" | "confirm" | "recovery-codes";

export interface TotpEnrolmentWizardProps {
	/**
	 * "enrol" (default) starts a fresh TOTP secret. "replace" clears an existing
	 * (e.g. lost) authenticator first — used after a recovery-code login.
	 */
	mode?: "enrol" | "replace";
	onAlreadyEnabled: () => void;
	onComplete: () => void;
}

export const TotpEnrolmentWizard = ({
	onAlreadyEnabled,
	onComplete,
	mode = "enrol",
}: TotpEnrolmentWizardProps) => {
	const [step, setStep] = useState<WizardStep>("start");
	const [qrSvg, setQrSvg] = useState("");
	const [secret, setSecret] = useState("");
	const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

	const totpSetup = useTotpSetup();
	const totpReplace = useTotpReplace();
	const totpConfirm = useTotpConfirm();

	// Both mutations share the setup contract (password → fresh pending secret);
	// pick by mode so the start step is identical for enrol vs replace.
	const startMutation = mode === "replace" ? totpReplace : totpSetup;

	const handleStartSubmit = (password: string) => {
		startMutation.mutate(
			{ password },
			{
				onSuccess: (resource) => {
					setQrSvg(resource.qr_svg);
					setSecret(resource.secret);
					setStep("confirm");
				},
			}
		);
	};

	const handleConfirmSubmit = (code: string) => {
		totpConfirm.mutate(
			{ code },
			{
				onSuccess: (resource) => {
					setRecoveryCodes(resource.recovery_codes);
					setStep("recovery-codes");
				},
				onError: (error) => {
					if (error instanceof MfaAlreadyEnabledError) {
						onAlreadyEnabled();
					}
				},
			}
		);
	};

	return (
		<div className="flex flex-col gap-6" data-testid="totp-enrolment-wizard">
			{step === "start" && (
				<TotpStartStep
					error={startMutation.error?.message}
					isSubmitting={startMutation.isPending}
					onSubmit={handleStartSubmit}
				/>
			)}
			{step === "confirm" && (
				<div className="flex flex-col gap-4">
					<div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start sm:justify-center">
						<div className="flex flex-col gap-2">
							<div
								className="flex justify-center [&_svg]:h-40 [&_svg]:w-40"
								data-testid="totp-qr-code"
								// qr_svg is generated server-side by TotpSetupResource, same trust boundary as any other BE-rendered markup
								dangerouslySetInnerHTML={{ __html: qrSvg }}
							/>
							<p className="break-all text-center font-mono text-xs text-gray-500" data-testid="totp-manual-secret">
								{secret}
							</p>
						</div>
						<div className="sm:border-l sm:border-gray-200 sm:pl-6">
							<AuthenticatorAppsHint />
						</div>
					</div>
					<TotpConfirmStep
						error={
							totpConfirm.error && !(totpConfirm.error instanceof MfaAlreadyEnabledError)
								? totpConfirm.error.message
								: undefined
						}
						isSubmitting={totpConfirm.isPending}
						onSubmit={handleConfirmSubmit}
					/>
				</div>
			)}
			{step === "recovery-codes" && (
				<RecoveryCodesStep codes={recoveryCodes} onDone={onComplete} />
			)}
		</div>
	);
};
